import {
  Box,
  Form,
  Text,
  TextInput,
  ColorPickerDropdown as TimelyColorPickerDropdown,
} from '@timelyapp/react';
import { VALIDATION_MESSAGES, VALIDATION_RULES } from 'constants/client';
import React from 'react';
import type { ClientDraft } from 'types/client';

type ClientFormVariant = 'modal' | 'dropdown';

interface Props {
  draft: ClientDraft;
  onUpdate: (clientDraft: ClientDraft) => unknown;
  onSubmit: () => unknown;
  asForm?: boolean;
  renderColorPicker?: (props: {
    color: string;
    onChange: (color: string) => void;
  }) => React.ReactNode;
  variant?: ClientFormVariant;
}

const CLIENT_NAME_INPUT_ID = 'client-name-client-form';

const ClientForm: React.FC<Props> = ({
  asForm = true,
  draft,
  onSubmit,
  onUpdate,
  renderColorPicker,
  variant = 'modal',
}) => {
  const isDropdown = variant === 'dropdown';

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({
      ...draft,
      name: e.target.value,
    });
  };

  const handleColorChange = (color: string) => {
    onUpdate({
      ...draft,
      color,
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const content = (
    <Box.Column gap={isDropdown ? 'medium' : 'large'} padding={isDropdown ? 'large' : 'xlarge'}>
      {draft.name.length >= VALIDATION_RULES.NAME_MAX_LENGTH && (
        <Text.Label color="danger" size="small">
          {VALIDATION_MESSAGES.NAME_MAX_LENGTH}
        </Text.Label>
      )}

      <Box.Row alignItems="flex-end" gap="large">
        <Box.Column flexGrow={1} gap="medium" id="tour_project_name">
          <Form.Label htmlFor={CLIENT_NAME_INPUT_ID}>Client name</Form.Label>
          <TextInput
            autoFocus
            className="fs-mask"
            data-testid="client_input_client_form"
            id={CLIENT_NAME_INPUT_ID}
            maxLength={VALIDATION_RULES.NAME_MAX_LENGTH}
            onChange={handleChangeName}
            placeholder="Client name"
            required
            value={draft.name}
          />
        </Box.Column>

        <Box.Column gap="medium" id="tour_project_color">
          <Form.Label>Color</Form.Label>
          {renderColorPicker ? (
            renderColorPicker({ color: draft.color, onChange: handleColorChange })
          ) : (
            <TimelyColorPickerDropdown color={draft.color} onChange={handleColorChange} />
          )}
        </Box.Column>
      </Box.Row>

      {!isDropdown && (
        <Box.Column gap="medium">
          <Text.Paragraph color="subtle" size="small">
            Changes to client color affect all projects for this client that use client color.
          </Text.Paragraph>
        </Box.Column>
      )}
    </Box.Column>
  );

  if (!asForm) {
    return content;
  }

  return <Form onSubmit={handleSubmit}>{content}</Form>;
};

export default ClientForm;
